/**
 * Player settings (Phases 16–19): kept in this browser, validated on read,
 * every storage call guarded (private windows just get the defaults).
 */
export interface Settings {
  /** Master volume 0..1 for sound effects. */
  readonly volume: number;
  readonly sound: boolean;
  /** Short vibrations on phones for important events (where supported). */
  readonly vibration: boolean;
  /** 'low' drops smoke / fire particles and damage numbers for weak phones. */
  readonly quality: 'high' | 'low';
  /** Interface language (D119); first visit follows the browser. */
  readonly language: 'en' | 'ar';
  /** Procedural soundtrack (D121). */
  readonly music: boolean;
  readonly musicVolume: number;
}

const browserArabic = typeof navigator !== 'undefined' && /^ar\b/i.test(navigator.language ?? '');
export const DEFAULT_SETTINGS: Settings = { volume: 0.6, sound: true, vibration: true, quality: 'high', language: browserArabic ? 'ar' : 'en', music: true, musicVolume: 0.5 };
const KEY = 'atf-settings';

export function sanitizeSettings(raw: unknown): Settings {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<Record<keyof Settings, unknown>>;
  const vol = typeof r.volume === 'number' && Number.isFinite(r.volume) ? Math.max(0, Math.min(1, r.volume)) : DEFAULT_SETTINGS.volume;
  return {
    volume: Math.round(vol * 100) / 100,
    sound: typeof r.sound === 'boolean' ? r.sound : DEFAULT_SETTINGS.sound,
    vibration: typeof r.vibration === 'boolean' ? r.vibration : DEFAULT_SETTINGS.vibration,
    quality: r.quality === 'low' ? 'low' : 'high',
    language: r.language === 'ar' || r.language === 'en' ? r.language : DEFAULT_SETTINGS.language,
    music: typeof r.music === 'boolean' ? r.music : DEFAULT_SETTINGS.music,
    musicVolume: typeof r.musicVolume === 'number' && Number.isFinite(r.musicVolume) ? Math.round(Math.max(0, Math.min(1, r.musicVolume)) * 100) / 100 : DEFAULT_SETTINGS.musicVolume,
  };
}

let cached: Settings | null = null;
const listeners = new Set<(s: Settings) => void>();

export function loadSettings(): Settings {
  if (cached) return cached;
  try {
    cached = sanitizeSettings(JSON.parse(localStorage.getItem(KEY) ?? 'null'));
  } catch {
    cached = DEFAULT_SETTINGS;
  }
  return cached;
}

export function saveSettings(next: Partial<Settings>): Settings {
  cached = sanitizeSettings({ ...loadSettings(), ...next });
  try {
    localStorage.setItem(KEY, JSON.stringify(cached));
  } catch {
    /* storage blocked: the change still applies to this session */
  }
  for (const f of listeners) f(cached);
  return cached;
}

export function onSettings(f: (s: Settings) => void): () => void {
  listeners.add(f);
  return () => listeners.delete(f);
}

/** For tests: forget the cached copy. */
export function resetSettingsCache(): void {
  cached = null;
}
